import { Component, h, Method, Prop, State } from '@stencil/core';
import { MenuContentDTO, Sources } from './interfaces';
import { Product } from "@salla.sa/twilight/types/common"
import { FooterMenu } from '../../../../twilight/types/api/component';
import Helper from '../../Helpers/Helper';

@Component({
  tag: 'salla-menu',
  styleUrl: 'salla-menu.scss',
})
export class SallaMenu {
  componentWillLoad() {
    return Helper.onSallaReadyPromise((resolve) => {
      this.hasCustomComponent = !!customElements.get('custom-salla-product-card');
      if (!!this.sourceValue) {
        this.setMenuContent(JSON.parse(this.sourceValue));
      } else {
        this.getMenuItems();
      }
      resolve();
    })
  }

  /**
   * The source of the menu, specifying whether it is a header or footer menu.
   */
  @Prop() source: Sources.Header | Sources.Footer | Sources.Footer = Sources.Header

  /**
   * The source value, a stringified JSON representation of the menu content.
   */
  @Prop() sourceValue?: string;

  /**
   * Boolean indicating whether the menu is a top navigation menu.
   */
  @Prop() topnav: boolean;

  /**
   * Boolean indicating whether to use React Link elements for menu links.
   */
  @Prop() useReactLink: boolean = false;

  /**
   * Limiting the number of menu items
   */
  @Prop() limit: number;


  // States  
  @State() menus: MenuContentDTO[] = [];
  @State() hasCustomComponent: boolean;
  // @State() pageLinkLabel: string;

  private getMenuItems() {
    salla.api.component.getMenus(this.source).then(resp => {
      // TODO: this has to be removed after the the api's consistency issue is fixed
      // FROM HERE
      let menusData = resp.data;
      if (!Array.isArray(menusData)) {
        let menusArray = Object.keys(menusData).map((key: string) => {
          return menusData[key]
        })
        return this.setMenuContent(menusArray)
      }
      // TILL HERE
      return this.setMenuContent(resp.data)
    });
  }

  private getAttributes(attrs: string) {
    return attrs.trim().split(' ').reduce((result, attr) => {
      let [key, value] = attr.split('=');
      result[key] = value.replace(/"/g, '');
      return result;
    }, {});
  }

  private getLinkTag(menu: MenuContentDTO, additionalClass: string = "") {
    if (this.useReactLink) {
      let link = document.createElement("Link")
      let attributes = this.getAttributes(menu.link_attrs)
      Object.keys(attributes).forEach(attr => {
        link.setAttribute(attr, attributes[attr]);
      });
      link.innerHTML = menu.title;
      link.classList.add(additionalClass)
      return link;
    }
    return <a {...this.getAttributes(menu.link_attrs)} class={additionalClass} href={menu.url}>
      <span>{menu.title}</span>
    </a>
  }

  /**
   * Updates the internal state of the component with the provided menu data.
   *
   * @param {MenuContentDTO[]} menus - An array of MenuContentDTO objects representing the new menu content.
   */
  @Method()
  async setMenuContent(menus: MenuContentDTO[]): Promise<void> {
    this.menus = menus
  }

  private getListItemClass(menu: MenuContentDTO, isRootMenu: boolean = false) {
    return {
      "s-menu-item": true,
      "s-menu-root-level": isRootMenu,
      "s-menu-non-root-level": !isRootMenu,
      "s-menu-has-children": !!menu.has_children,
      "s-menu-mega-menu": !!menu.products
    }
  }

  private _get_header_menu(menu: MenuContentDTO, isRootMenu: boolean = false) {
    return [<li class={this.getListItemClass(menu, isRootMenu)}
      {...this.getAttributes(menu.attrs)}>
      {
        this.getLinkTag(menu)
      }
      {
        menu.has_children ?
          <div class={`s-menu-submenu ${!!menu.products ? 's-menu-submenu-with-product' : 's-menu-submenu-without-product'}}`}>
            <ul class={`${!!menu.products ? 's-menu-submenu-unordered-list' : ''}`}>
              {
                menu.children.map((submenu: MenuContentDTO) =>
                  this._get_header_menu(submenu)
                )
              }
            </ul>

            {
              !!menu.products ?
                <div class="s-menu-products-wrapper">
                  <div class="s-menu-products-container">
                    {
                      menu.products.slice(0, 4).map((product: Product) => {
                        if (this.hasCustomComponent) {
                          return <custom-salla-product-card product={JSON.stringify(product)}></custom-salla-product-card>
                        }
                        <salla-product-card shadow-on-hover product={JSON.stringify(product)}></salla-product-card>
                      })
                    }
                  </div>
                </div>
                : ""
            }

          </div>
          : ''
      }
    </li>,

    <li class="lg:hidden text-sm font-bold" {...this.getAttributes(menu.attrs)}>
      {!menu.has_children ?
        this.getLinkTag(menu, "text-gray-500")
        :
        [<span>{menu.title}</span>,
        <ul>
          <li class="text-sm font-bold">
            {this.getLinkTag(menu, "text-gray-500")}
          </li>
          {
            menu.children.map((submenu: MenuContentDTO) =>
              this._get_header_menu(submenu)
            )
          }
        </ul>]
      }
    </li>
    ];
  }

  private _get_footer_menu(menus: FooterMenu[]) {
    let items = this.topnav ? menus.slice(0, 3) : menus
    return <div>
      <div class={{
        "s-menu-footer-list": !this.topnav,
        "s-menu-topnav-list": this.topnav
      }}
      >
        {((this.topnav || this.limit) ? items.slice(0, this.limit || 3) : items).map((menu: FooterMenu) => {
          return <a href={menu.url} target={menu.target} class={{
            "s-menu-footer-item": !this.topnav,
            "s-menu-topnav-item topnav-link-item right-side": this.topnav
          }}>{menu.title}</a>
        })}
      </div>
    </div>
  }

  render() {

    if (this.source === Sources.Footer) {
      return this._get_footer_menu(this.menus)
    }
    return <ul>
      {this.menus.map((menu: MenuContentDTO) => {
        return this._get_header_menu(menu, true)
      })
      }
    </ul>
  }
}
